import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosClient from '../api/axiosClient';
import {
  PageHeader, KPIGrid, Card, Btn, Tabs,
  DataTable, ContentSection, EmptyState,
  colors, spacing, typography, radius, shadows,
} from '../design-system';

// ── Inline alert (avoids message-only Alert API constraint) ────────────────────

function Banner({ type, children }) {
  const styles = {
    danger:  { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' },
    warning: { bg: '#fef3c7', text: '#92400e', border: '#fde047' },
    info:    { bg: '#e0f2fe', text: '#0369a1', border: '#7dd3fc' },
  };
  const s = styles[type] || styles.info;
  return (
    <div style={{
      background: s.bg, color: s.text,
      border: `1px solid ${s.border}`,
      borderRadius: radius.md,
      padding: `${spacing.sm} ${spacing.md}`,
      fontSize: typography.fontSizes.md,
      fontWeight: typography.fontWeights.medium,
      marginBottom: spacing.md,
    }}>
      {children}
    </div>
  );
}

// ── NavCard ────────────────────────────────────────────────────────────────────

function NavCard({ icon, label, description, to, href, badge, accentColor }) {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);
  const accent = accentColor || colors.primary;

  return (
    <div
      onClick={() => href ? window.open(href, "_blank", "noreferrer") : navigate(to)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: colors.surface,
        border: `1px solid ${hovered ? accent : colors.border}`,
        borderRadius: radius.lg,
        padding: `${spacing.md} ${spacing.lg}`,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: spacing.md,
        transition: 'all 150ms ease',
        transform: hovered ? 'translateY(-2px)' : 'none',
        boxShadow: hovered ? shadows.md : shadows.cardShadow,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Left accent stripe */}
      <div style={{
        position: 'absolute', top: 0, left: 0, bottom: 0, width: 3,
        background: accent,
      }} />

      <span style={{ fontSize: '20px', flexShrink: 0 }}>{icon}</span>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: typography.fontSizes.md,
          fontWeight: typography.fontWeights.semibold,
          color: colors.textPrimary,
          lineHeight: 1.2,
        }}>
          {label}
        </div>
        {description && (
          <div style={{
            fontSize: typography.fontSizes.xs,
            color: colors.textMuted,
            marginTop: 2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {description}
          </div>
        )}
      </div>

      {badge != null && badge > 0 && (
        <span style={{
          background: '#fee2e2',
          color: '#991b1b',
          borderRadius: radius.pill,
          fontSize: typography.fontSizes.xs,
          fontWeight: typography.fontWeights.bold,
          padding: '2px 8px',
          flexShrink: 0,
        }}>
          {badge}
        </span>
      )}

      <span style={{ color: hovered ? accent : colors.textMuted, fontSize: '14px', flexShrink: 0 }}>›</span>
    </div>
  );
}

// ── NavGroup ───────────────────────────────────────────────────────────────────

function NavGroup({ icon, title, subtitle, children, accentColor }) {
  return (
    <div style={{ marginBottom: spacing.xl }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.sm }}>
        <span style={{ fontSize: '18px' }}>{icon}</span>
        <div>
          <div style={{
            fontSize: typography.fontSizes.md,
            fontWeight: typography.fontWeights.bold,
            color: colors.textPrimary,
          }}>
            {title}
          </div>
          {subtitle && (
            <div style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted }}>{subtitle}</div>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
        {React.Children.map(children, child =>
          child ? React.cloneElement(child, {
            accentColor: child.props.accentColor || accentColor,
          }) : null
        )}
      </div>
    </div>
  );
}

// ── Activity Feed ──────────────────────────────────────────────────────────────

function ActivityFeed({ activities }) {
  if (!activities || activities.length === 0) {
    return <EmptyState icon="📋" title="No recent activity" subtitle="Platform events will appear here." />;
  }
  return (
    <div>
      {activities.slice(0, 12).map((a, i) => (
        <div key={i} style={{
          display: 'flex',
          gap: spacing.sm,
          padding: `${spacing.sm} 0`,
          borderBottom: i < Math.min(activities.length, 12) - 1
            ? `1px solid ${colors.border}` : 'none',
        }}>
          <div style={{
            width: 8, height: 8, borderRadius: '50%',
            background: colors.primary,
            flexShrink: 0, marginTop: 6,
          }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: typography.fontSizes.sm, color: colors.textPrimary, lineHeight: 1.4 }}>
              {a.description || 'System event'}
            </div>
            <div style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted, marginTop: 2 }}>
              {a.actor?.name || 'System'} · {a.created_at ? new Date(a.created_at).toLocaleString() : ''}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── CardWithHeader ─────────────────────────────────────────────────────────────

function CardWithHeader({ title, subtitle, children, style }) {
  return (
    <Card style={style}>
      {(title || subtitle) && (
        <div style={{ marginBottom: spacing.md }}>
          {title && (
            <div style={{
              fontSize: typography.fontSizes.lg,
              fontWeight: typography.fontWeights.semibold,
              color: colors.textPrimary,
            }}>
              {title}
            </div>
          )}
          {subtitle && (
            <div style={{ fontSize: typography.fontSizes.sm, color: colors.textMuted }}>{subtitle}</div>
          )}
        </div>
      )}
      {children}
    </Card>
  );
}

// ── Stat mini-card ─────────────────────────────────────────────────────────────

function StatMini({ label, value, color }) {
  return (
    <div style={{
      background: colors.background,
      borderRadius: radius.md,
      padding: `${spacing.md} ${spacing.lg}`,
      border: `1px solid ${colors.border}`,
    }}>
      <div style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted, marginBottom: 4 }}>{label}</div>
      <div style={{
        fontSize: typography.fontSizes.xxl,
        fontWeight: typography.fontWeights.bold,
        color: color || colors.textPrimary,
      }}>
        {value}
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

const TABS = [
  { key: 'overview',      label: 'Overview' },
  { key: 'accreditation', label: 'Accreditation' },
  { key: 'governance',    label: 'Governance' },
  { key: 'system',        label: 'System' },
];

// CB Performance table columns
const CB_COLS = [
  { key: 'cert_body_name',            label: 'Certification Body', render: v => v || 'Unknown' },
  { key: 'total_certificates_issued', label: 'ISO Certs',          render: v => v ?? 0 },
  { key: 'stage_1_count',             label: 'Stage 1 Audits',     render: v => v ?? 0 },
  { key: 'stage_2_count',             label: 'Stage 2 Audits',     render: v => v ?? 0 },
  { key: 'client_count',              label: 'Clients',            render: v => v ?? 0 },
  { key: 'revenue_generated',         label: 'Revenue',            render: v => `£${(v || 0).toFixed(0)}` },
  {
    key: 'sla_compliance_rate',
    label: 'SLA Compliance',
    render: (v) => {
      const pct = v || 0;
      const bg  = pct >= 95 ? '#dcfce7' : pct >= 80 ? '#fef3c7' : '#fee2e2';
      const col = pct >= 95 ? '#166534' : pct >= 80 ? '#92400e' : '#991b1b';
      return (
        <span style={{ background: bg, color: col, padding: '2px 8px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
          {pct.toFixed(1)}%
        </span>
      );
    },
  },
];

export default function SuperAdminDashboard() {
  const [appData, setAppData] = useState({
    overview: null, slaMetrics: null,
    certBodyPerformance: [], activityFeed: [],
    surveillanceOverdue: 0, openRenewals: 0,
  });
  const [loading, setLoading]     = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab]  = useState('overview');

  const fetchAll = async () => {
    const results = await Promise.allSettled([
      axiosClient.get('/super-admin/dashboard/overview'),
      axiosClient.get('/super-admin/dashboard/sla-metrics'),
      axiosClient.get('/super-admin/dashboard/cert-body-performance?period=30d'),
      axiosClient.get('/super-admin/dashboard/activity-feed?limit=30'),
      axiosClient.get('/surveillance/?status=overdue'),
      axiosClient.get('/accreditation-renewals/?status=open'),
    ]);
    const get = (r) => (r.status === 'fulfilled' ? r.value.data : null);
    const overdueList  = get(results[4]);
    const renewalsList = get(results[5]);
    setAppData({
      overview:            get(results[0]),
      slaMetrics:          get(results[1]),
      certBodyPerformance: get(results[2]) || [],
      activityFeed:        get(results[3])?.activities || [],
      surveillanceOverdue: Array.isArray(overdueList)  ? overdueList.length  : 0,
      openRenewals:        Array.isArray(renewalsList) ? renewalsList.length : 0,
    });
  };

  useEffect(() => {
    setLoading(true);
    fetchAll().finally(() => setLoading(false));
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAll();
    setRefreshing(false);
  };

  const ov  = appData.overview;
  const cbs = ov?.cert_bodies_summary || {};
  const sys = ov?.system_health || {};
  const fin = ov?.financial_overview || {};
  const sla = appData.slaMetrics?.compliance_scores || {};

  const kpis = [
    { label: 'Certification Bodies',  value: loading ? '…' : (cbs.total_cert_bodies     ?? '—'), icon: '🏢', color: colors.primary  },
    { label: 'Total Certificates',    value: loading ? '…' : (cbs.total_certificates_issued ?? '—'), icon: '📜', color: '#7c3aed'  },
    { label: 'Active Clients',        value: loading ? '…' : (cbs.total_clients           ?? '—'), icon: '👥', color: '#059669'  },
    { label: 'Active Users Today',    value: loading ? '…' : (sys.active_users_today      ?? '—'), icon: '🟢', color: colors.info    },
    {
      label: 'SLA Compliance',
      value: loading ? '…' : (sla.overall_compliance_percentage != null
        ? `${sla.overall_compliance_percentage.toFixed(1)}%` : '—'),
      icon: '⏱️',
      color: sla.overall_compliance_percentage >= 95 ? colors.success
           : sla.overall_compliance_percentage >= 80 ? colors.warning
           : colors.danger,
    },
    {
      label: 'Pending Revenue',
      value: loading ? '…' : (fin.accreditation_fees?.total_pending != null
        ? `£${fin.accreditation_fees.total_pending.toFixed(0)}` : '—'),
      icon: '💳',
      color: colors.success,
    },
  ];

  return (
    <div>
      {/* Page header */}
      <PageHeader
        title="System Dashboard"
        subtitle="Centralised oversight of accreditation, governance, financials, and platform health."
        actions={
          <Btn variant="secondary" onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? '↺ Refreshing…' : '↺ Refresh'}
          </Btn>
        }
      />

      {/* Alert banners */}
      {appData.surveillanceOverdue > 0 && (
        <Banner type="danger">
          <strong>{appData.surveillanceOverdue} overdue surveillance audit{appData.surveillanceOverdue !== 1 ? 's' : ''}</strong>
          {' — '}
          <a href="/governance/surveillance" style={{ color: 'inherit', fontWeight: 700 }}>
            Go to Surveillance →
          </a>
        </Banner>
      )}
      {appData.openRenewals > 0 && (
        <Banner type="warning">
          <strong>{appData.openRenewals} open accreditation renewal{appData.openRenewals !== 1 ? 's' : ''}</strong> require action{' — '}
          <a href="/governance/renewals" style={{ color: 'inherit', fontWeight: 700 }}>
            Go to Renewals →
          </a>
        </Banner>
      )}

      {/* KPI strip */}
      <KPIGrid kpis={kpis} columns={6} />

      {/* Tab bar */}
      <Tabs tabs={TABS} activeTab={activeTab} onChange={setActiveTab} />

      {/* ── Overview ── */}
      {activeTab === 'overview' && (
        <div className="ds-dash-grid-sidebar">
          <ContentSection title="Certification Body Performance" subtitle="SLA compliance and activity for the last 30 days.">
            <DataTable
              columns={CB_COLS}
              rows={appData.certBodyPerformance}
              loading={loading}
              emptyMessage="No certification body data available."
            />
          </ContentSection>

          <CardWithHeader title="Recent Activity" subtitle="Latest platform events">
            <ActivityFeed activities={appData.activityFeed} />
          </CardWithHeader>
        </div>
      )}

      {/* ── Accreditation ── */}
      {activeTab === 'accreditation' && (
        <div className="ds-dash-grid-2">
          <NavGroup icon="🏛️" title="Accreditation Management" subtitle="CB and auditor accreditations" accentColor={colors.primary}>
            <NavCard icon="🏢" label="Manage Cert Bodies"         description="Full list and detail for all CBs"         to="/accreditation/cert-bodies" />
            <NavCard icon="👤" label="Manage Auditors"            description="Auditor roster and accreditation status"  to="/accreditation/auditors" />
            <NavCard icon="📚" label="Accredited Courses"         description="Courses approved for CPD/competency"      to="/accreditation/courses" />
            <NavCard icon="✅" label="Course Accreditations"      description="Review and approve course submissions"    to="/accreditation/course-accreditations" />
            <NavCard icon="♻️" label="Monitoring & Renewal"       description="Renewal cycles, visits, spot checks"      to="/accreditation/monitoring" />
            <NavCard icon="📋" label="Accreditation Scopes"       description="Define and manage scope categories"       to="/accreditation/scopes" />
            <NavCard icon="📏" label="Accreditation Rules"        description="Governance rules and thresholds"          to="/accreditation/rules" />
          </NavGroup>

          <NavGroup icon="📜" title="Certificate Management" subtitle="Issue and track all certificate types" accentColor="#7c3aed">
            <NavCard icon="📜" label="Certificate Manager"        description="Issue and manage ISO certificates"        to="/certificates" />
            <NavCard icon="📈" label="Lifecycle Dashboard"        description="Track certificate status over time"       to="/certificate-lifecycle" />
            <NavCard icon="🏆" label="ISO Certificates"           description="All issued ISO standard certificates"     to="/iso-certificates" />
            <NavCard icon="🎓" label="CPD Certificates"           description="Continuing professional development certs" to="/cpd-certificates" />
            <NavCard icon="📝" label="Company Assessments"        description="Assessment results and certificates"      to="/company-assessments" />
            <NavCard icon="🎖️" label="Accreditation Certificates" description="Formal ICSC accreditation certificates"   to="/governance/acc-certificates" />
          </NavGroup>
        </div>
      )}

      {/* ── Governance ── */}
      {activeTab === 'governance' && (
        <div className="ds-dash-grid-2">
          <NavGroup icon="🔍" title="Oversight & Monitoring" subtitle="Active compliance and audit management" accentColor={colors.warning}>
            <NavCard
              icon="🔍" label="Surveillance Schedule"
              description="Ongoing and overdue surveillance audits"
              to="/governance/surveillance"
              badge={appData.surveillanceOverdue}
              accentColor={appData.surveillanceOverdue > 0 ? colors.danger : colors.success}
            />
            <NavCard
              icon="🔄" label="Accreditation Renewals"
              description="Renewals open and pending action"
              to="/governance/renewals"
              badge={appData.openRenewals}
              accentColor={appData.openRenewals > 0 ? colors.warning : colors.success}
            />
            <NavCard icon="📊" label="Auditor Performance"         description="Score records and flagged auditors"      to="/governance/auditor-performance" />
            <NavCard icon="🎖️" label="Accreditation Certificates"  description="View and generate accreditation certs"   to="/governance/acc-certificates" />
          </NavGroup>

          <NavGroup icon="👥" title="Professional Membership" subtitle="ICSC membership applications and lifecycle" accentColor="#8b5cf6">
            <NavCard icon="👥" label="Membership Dashboard"        description="Active members, applications, and billing"  to="/membership" />
            <NavCard icon="✓"  label="Verify a Membership"        description="Public membership verification on ICSC website"  href="https://councilforstandards.org/verify/membership" />
          </NavGroup>

          <NavGroup icon="💳" title="Financial & SLAs" subtitle="Revenue, invoicing, and service levels" accentColor={colors.success}>
            <NavCard icon="💳" label="Invoices"                    description="Pending and paid invoice management"    to="/invoices" />
            <NavCard icon="⏱️" label="SLA Management"              description="Service level agreements and breaches"  to="/sla-management" />
            <NavCard icon="🏢" label="Cert Bodies"                 description="CB profiles and compliance status"      to="/cert-bodies" />
            <NavCard icon="🔎" label="Reviewer Dashboard"          description="CB portal submission review queue"      to="/reviewer-dashboard" />
          </NavGroup>
        </div>
      )}

      {/* ── System ── */}
      {activeTab === 'system' && (
        <div className="ds-dash-grid-2">
          <NavGroup icon="⚙️" title="Platform Administration" subtitle="Users, settings, and audit trail" accentColor={colors.info}>
            <NavCard icon="👥" label="User Management"             description="Create, edit, and manage all users"     to="/users" />
            <NavCard icon="⚙️" label="Settings"                    description="Platform-wide configuration"            to="/settings" />
            <NavCard icon="🔒" label="Security Audit"              description="Audit log for all privileged actions"   to="/audit-logs" />
            <NavCard icon="📊" label="Analytics"                   description="Usage and performance analytics"        to="/analytics" />
          </NavGroup>

          <NavGroup icon="📚" title="Learning Platform" subtitle="LMS and competency management" accentColor="#059669">
            <NavCard icon="🎬" label="Course Manager"              description="Create and publish LMS courses"         to="/courses" />
            <NavCard icon="📦" label="SCORM Import"                description="Import SCORM-compliant course packages"  to="/scorm-import" />
            <NavCard icon="📋" label="Assign Courses"              description="Assign courses to users or CBs"         to="/assign-courses" />
            <NavCard icon="🎯" label="Competency Courses"          description="Manage competency framework courses"     to="/competency/courses" />
            <NavCard icon="📄" label="ISO Documents"               description="ISO document library"                   to="/documents" />
          </NavGroup>

          {/* System health panel */}
          <CardWithHeader title="System Health" subtitle="Infrastructure status" style={{ gridColumn: '1 / -1' }}>
            <div className="ds-stat-strip">
              <StatMini label="Database"      value="Healthy" color={colors.success} />
              <StatMini label="API Response"  value={sys.api_response_time_ms ? `${sys.api_response_time_ms}ms` : '—'} />
              <StatMini label="Error Rate"    value={sys.error_rate != null ? `${(sys.error_rate * 100).toFixed(2)}%` : '—'}
                color={sys.error_rate < 0.05 ? colors.success : colors.danger} />
              <StatMini label="Active Users"  value={sys.active_users_today ?? '—'}     color={colors.primary} />
              <StatMini label="Cert Bodies"   value={cbs.total_cert_bodies ?? '—'}      color={colors.primary} />
              <StatMini label="SLA Rate"      value={sla.overall_compliance_percentage != null
                ? `${sla.overall_compliance_percentage.toFixed(1)}%` : '—'}
                color={sla.overall_compliance_percentage >= 90 ? colors.success : colors.warning} />
            </div>
          </CardWithHeader>
        </div>
      )}
    </div>
  );
}
